import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Users, Ticket, Trophy, Sparkles, Clock } from 'lucide-react';
import Confetti from 'react-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EventDetails() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [pendingReceipts, setPendingReceipts] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [winners, setWinners] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [drawExecuted, setDrawExecuted] = useState(false);

  useEffect(() => {
    fetchEventDetails();
    fetchReceipts();
    fetchPendingReceipts();
    fetchCoupons();
    fetchWinners();
  }, [eventId]);

  const fetchEventDetails = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}`);
      setEvent(response.data);
    } catch (error) {
      console.error('Error fetching event:', error);
    }
  };

  const fetchReceipts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/receipts/event/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceipts(response.data);
    } catch (error) {
      console.error('Error fetching receipts:', error);
      setReceipts([]);
    }
  };

  const fetchPendingReceipts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/receipts/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter for current event only
      const eventPendingReceipts = response.data.filter(r => r.event_id === eventId);
      setPendingReceipts(eventPendingReceipts);
    } catch (error) {
      console.error('Error fetching pending receipts:', error);
      setPendingReceipts([]);
    }
  };

  const handleApproveReceipt = async (receiptId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/receipts/${receiptId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Receipt approved and coupons generated!');
      fetchPendingReceipts();
      fetchReceipts();
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve receipt');
    }
  };

  const handleRejectReceipt = async (receiptId, reason = 'Invalid receipt') => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/receipts/${receiptId}/reject?reason=${encodeURIComponent(reason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Receipt rejected');
      fetchPendingReceipts();
    } catch (error) {
      toast.error('Failed to reject receipt');
    }
  };

  const fetchCoupons = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/coupons/event/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoupons(response.data);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      setCoupons([]);
    }
  };

  const fetchWinners = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/winners`);
      if (response.data.draw_executed) {
        setWinners(response.data.winners);
        setDrawExecuted(true);
      }
    } catch (error) {
      console.error('Error fetching winners:', error);
    }
  };

  const handleExecuteDraw = async () => {
    if (coupons.length === 0) {
      toast.error('No coupons available for draw');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/events/${eventId}/draw`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWinners(response.data.winners);
      setShowConfetti(true);
      setDrawExecuted(true);
      toast.success('Draw executed successfully!');
      setTimeout(() => setShowConfetti(false), 8000);
      fetchEventDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to execute draw');
    }
  };

  if (!event) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <p className="font-outfit text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pt-6 pb-12" data-testid="event-details-page">
      {showConfetti && <Confetti recycle={false} numberOfPieces={800} />}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          onClick={() => navigate('/business/dashboard')}
          variant="ghost"
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="mb-6 sm:mb-8">
          <h1 className="font-playfair font-bold text-2xl sm:text-3xl lg:text-4xl text-primary mb-2" data-testid="event-title">
            {event.event_name}
          </h1>
          <p className="font-outfit text-sm sm:text-base text-gray-600">{event.event_description}</p>
          <div className="flex flex-wrap gap-2 sm:gap-4 mt-4">
            <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs sm:text-sm font-outfit">
              Incentive Points: {event.coupon_value}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs sm:text-sm font-outfit ${
              event.status === 'active' ? 'bg-green-100 text-green-800' :
              event.status === 'completed' ? 'bg-gray-100 text-gray-800' :
              'bg-yellow-100 text-yellow-800'
            }`}>
              {event.status}
            </span>
          </div>
        </div>

        {/* Prizes Section */}
        {event.prizes && event.prizes.length > 0 && (
          <Card className="p-4 sm:p-6 mb-6 sm:mb-8 bg-gradient-to-br from-amber-50 to-orange-50">
            <h3 className="font-playfair font-bold text-xl sm:text-2xl text-primary mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
              Event Prizes
            </h3>
            <div className="space-y-3">
              {event.prizes.map((prize, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm">
                  <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-amber-100 text-amber-700 font-bold rounded-full text-sm">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-primary text-base sm:text-lg break-words">
                      {prize.prize_name || `Prize ${idx + 1}`}
                    </p>
                    {prize.prize_description && (
                      <p className="text-sm text-gray-600 mt-1 break-words">{prize.prize_description}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="font-bold text-amber-600 text-lg sm:text-xl">${prize.prize_amount}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <Card className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-outfit text-xs sm:text-sm text-gray-600 mb-1">Total Receipts</p>
                <p className="font-playfair text-2xl sm:text-3xl font-bold text-primary" data-testid="receipts-count">{receipts.length}</p>
              </div>
              <Users className="w-6 h-6 sm:w-8 sm:h-8 text-amber-600" />
            </div>
          </Card>
          <Card className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-outfit text-xs sm:text-sm text-gray-600 mb-1">Incentive Credits</p>
                <p className="font-playfair text-2xl sm:text-3xl font-bold text-amber-600" data-testid="coupons-count">{coupons.length}</p>
              </div>
              <Ticket className="w-6 h-6 sm:w-8 sm:h-8 text-amber-600" />
            </div>
          </Card>
          <Card className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-outfit text-xs sm:text-sm text-gray-600 mb-1">Winners</p>
                <p className="font-playfair text-2xl sm:text-3xl font-bold text-primary" data-testid="winners-count">{winners.length}</p>
              </div>
              <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-amber-600" />
            </div>
          </Card>
        </div>

        {event.status === 'active' && !drawExecuted && coupons.length > 0 && new Date() > new Date(event.end_date) && (
          <Card className="p-4 sm:p-6 mb-6 sm:mb-8 bg-gradient-to-r from-amber-50 to-yellow-50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="font-playfair font-bold text-xl sm:text-2xl text-primary mb-2">Ready for Lucky Draw</h3>
                <p className="font-outfit text-sm sm:text-base text-gray-600">{coupons.length} coupons are eligible for the draw</p>
              </div>
              <Button
                onClick={handleExecuteDraw}
                className="bg-amber-600 hover:bg-amber-700 text-base sm:text-lg px-6 sm:px-8 w-full sm:w-auto"
                data-testid="execute-draw-button"
              >
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Execute Draw
              </Button>
            </div>
          </Card>
        )}

        {/* Event Still Active - Show Waiting Message */}
        {event.status === 'active' && !drawExecuted && coupons.length > 0 && new Date() <= new Date(event.end_date) && (
          <Card className="p-4 sm:p-6 mb-6 sm:mb-8 bg-blue-50 border-blue-200">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-playfair font-bold text-lg sm:text-xl text-blue-900 mb-2">Draw Not Yet Available</h3>
                <p className="font-outfit text-sm sm:text-base text-blue-800 mb-2">
                  The lucky draw can only be executed after the event ends on <strong>{new Date(event.end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>.
                </p>
                <p className="font-outfit text-xs sm:text-sm text-blue-700">
                  Currently {coupons.length} coupon{coupons.length !== 1 ? 's are' : ' is'} eligible for the draw.
                </p>
              </div>
            </div>
          </Card>
        )}


        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="pending" className="flex-1 sm:flex-none">Pending Receipts ({pendingReceipts.length})</TabsTrigger>
            <TabsTrigger value="receipts" className="flex-1 sm:flex-none">Receipts ({receipts.length})</TabsTrigger>
            <TabsTrigger value="coupons" className="flex-1 sm:flex-none">Coupons ({coupons.length})</TabsTrigger>
            <TabsTrigger value="draw-results" className="flex-1 sm:flex-none">Draw Results</TabsTrigger>
          </TabsList>

          {/* Pending Receipts Tab */}
          <TabsContent value="pending">
            {pendingReceipts.length === 0 ? (
              <Card className="p-12 text-center">
                <p className="font-outfit text-gray-600">No pending receipts</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingReceipts.map((receipt) => (
                  <Card key={receipt.receipt_id} className="p-4 sm:p-6" data-testid={`pending-receipt-${receipt.receipt_id}`}>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                      <div className="flex-1">
                        <h3 className="font-playfair font-bold text-xl text-primary mb-2">
                          ${receipt.transaction_amount}
                        </h3>
                        <div className="space-y-1 text-sm font-outfit">
                          <p><strong>Event:</strong> {event.event_name}</p>
                          <p><strong>Customer:</strong> {receipt.customer_name}</p>
                          <p><strong>Date:</strong> {new Date(receipt.transaction_date).toLocaleDateString()}</p>
                          <p><strong>Business:</strong> {receipt.business_name}</p>
                          <p><strong>Submitted:</strong> {new Date(receipt.submitted_at).toLocaleString()}</p>
                        </div>
                        {receipt.receipt_image_url && (
                          <div className="mt-3">
                            <a 
                              href={`${BACKEND_URL}${receipt.receipt_image_url}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-sm underline"
                            >
                              📎 View Receipt Image
                            </a>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleApproveReceipt(receipt.receipt_id)}
                          className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700"
                          size="sm"
                        >
                          Approve
                        </Button>
                        <Button
                          onClick={() => handleRejectReceipt(receipt.receipt_id)}
                          variant="outline"
                          className="flex-1 sm:flex-none border-red-600 text-red-600 hover:bg-red-50"
                          size="sm"
                        >
                          Reject
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Approved Receipts Tab - Single Line Format with Email */}
          <TabsContent value="receipts">
            {receipts.length === 0 ? (
              <Card className="p-12 text-center">
                <p className="font-outfit text-gray-600">No receipts approved yet</p>
              </Card>
            ) : (
              <div className="space-y-2">
                {receipts.map((receipt) => (
                  <Card key={receipt.receipt_id} className="p-3 sm:p-4 hover:shadow-md transition-shadow" data-testid={`receipt-${receipt.receipt_id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 font-outfit text-xs sm:text-sm">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
                        <span><strong>Customer:</strong> {receipt.customer_name}</span>
                        <span><strong>Email:</strong> {receipt.customer_email || 'N/A'}</span>
                        <span><strong>Date:</strong> {new Date(receipt.transaction_date).toLocaleDateString()}</span>
                        <span><strong>Business:</strong> {receipt.business_name}</span>
                        <span><strong>Amount:</strong> ${receipt.transaction_amount}</span>
                      </div>
                      <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold self-start sm:self-auto">
                        Approved
                      </span>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="coupons">
            {coupons.length === 0 ? (
              <Card className="p-12 text-center">
                <Ticket className="w-16 h-16 mx-auto mb-4 text-amber-600" />
                <p className="font-outfit text-gray-600">No coupons generated yet</p>
              </Card>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {/* Sort: Winners first, then regular coupons */}
                {[...coupons].sort((a, b) => {
                  if (a.status === 'winner' && b.status !== 'winner') return -1;
                  if (a.status !== 'winner' && b.status === 'winner') return 1;
                  return 0;
                }).map((coupon) => (
                  <div
                    key={coupon.coupon_id}
                    className={`p-3 sm:p-4 rounded-lg text-center border-2 border-dashed ${
                      coupon.status === 'winner' 
                        ? 'bg-amber-100 border-amber-400' 
                        : 'bg-gray-50 border-gray-300'
                    }`}
                    data-testid={`coupon-${coupon.coupon_id}`}
                  >
                    <Ticket className={`w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-2 ${
                      coupon.status === 'winner' ? 'text-amber-600' : 'text-gray-400'
                    }`} />
                    <p className="font-outfit text-xs font-mono break-all">{coupon.coupon_id}</p>
                    {coupon.status === 'winner' && (
                      <p className="text-xs text-amber-700 font-semibold mt-1">WINNER</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Draw Results Tab */}
          <TabsContent value="draw-results">
            {!drawExecuted ? (
              <Card className="p-8 sm:p-12 text-center bg-gradient-to-r from-amber-50 to-yellow-50">
                <Clock className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-amber-600" />
                <h3 className="font-playfair font-bold text-xl sm:text-2xl text-primary mb-2">
                  Draw Not Executed Yet
                </h3>
                <p className="font-outfit text-gray-600 text-sm sm:text-base">
                  The lucky draw for this event has not been conducted yet. Winners will appear here once the draw is executed.
                </p>
              </Card>
            ) : winners.length === 0 ? (
              <Card className="p-8 sm:p-12 text-center">
                <p className="font-outfit text-gray-600">No winners yet</p>
              </Card>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                <div className="mb-4 sm:mb-6 text-center">
                  <Trophy className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-amber-600" />
                  <h3 className="font-playfair font-bold text-2xl sm:text-3xl text-primary mb-2">
                    🏆 Winners Announced!
                  </h3>
                  <p className="font-outfit text-gray-600 text-sm sm:text-base">
                    Congratulations to all winners!
                  </p>
                </div>
                {winners.map((winner, idx) => (
                  <Card key={idx} className="p-3 sm:p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200" data-testid={`winner-${idx}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 font-outfit text-xs sm:text-sm">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 lg:gap-6 flex-1">
                        <span className="px-3 py-1 bg-amber-600 text-white rounded-full font-bold self-start">#{idx + 1}</span>
                        <span className="font-playfair font-bold text-base sm:text-lg text-amber-700">{winner.prize_name}</span>
                        <span><strong>Prize:</strong> ${winner.prize_amount}</span>
                        <span className="sm:border-l sm:pl-4"><strong>Winner:</strong> {winner.customer_name || winner.user_id}</span>
                        <span><strong>Email:</strong> {winner.customer_email || 'N/A'}</span>
                        <span><strong>Phone:</strong> {winner.customer_phone || 'N/A'}</span>
                        <span className="text-amber-700 font-semibold"><strong>Coupon:</strong> {winner.coupon_id}</span>
                      </div>
                      <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-amber-600 self-end sm:self-auto" />
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
